import { afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild } from '@angular/core';
import { MpModalService } from './mp-modal.service';
import { CommonModule } from '@angular/common';
import { MpButtonComponent } from '../mp-button/mp-button.component';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { TranslateModule } from '@ngx-translate/core';
import { MP_MODAL_DATA } from './mp-modal.model';

@Component({
  selector: 'mp-modal',
  imports: [CommonModule, MpButtonComponent, CdkTrapFocus, TranslateModule],
  providers: [MpModalService],
  templateUrl: './mp-modal.component.html',
  styleUrls: ['./mp-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpModalComponent implements OnInit, OnDestroy {

  @ViewChild('modal', { read: ElementRef, static: false }) modalElementRef!: ElementRef<any>;

  @Input() public anchor: ElementRef;
  @Input() public mpModal: TemplateRef<any>;
  @Input() public mpModalTitle: string;
  @Input() public mpMode: 'centered' | 'sidenav' | 'floating' | 'from-bottom' = 'centered';
  @Input() public mpModalSaveLabel: string;
  @Input() public mpModalCancelLabel: string;
  @Input() public mpModalData: unknown;
  @Input() public mpModalBackdrop: boolean;
  @Input() public mpClickableModalBackdrop: boolean;
  @Input() public mpClosable: boolean;
  @Input() public mpShowCloseButton: boolean;
  @Input() public mpShowCancelButton: boolean;
  @Input() public mpDisplayHeader: boolean;
  @Input() public mpDisplayFooter: boolean;
  @Input() public mpDisplayFooterTopBorder: boolean;
  @Input() public removeModal: () => {};

  protected projectedInjector: Injector;

  constructor(private readonly ngZone: NgZone,
              private readonly injector: Injector,
              public readonly mpModalService: MpModalService
  ) {
    afterNextRender({ write: () => this.computePosition() });
  }

  ngOnInit() {
    this.mpModalService.setModalOpen();
    this.initProjectedContentInjector();
  }

  ngOnDestroy() {
    this.mpModalService.setModalClosed();
  }

  public backdropClicked() {
    if (this.mpClickableModalBackdrop) {
      this.removeModal();
    }
  }

  protected closeModal() {
    if (this.mpClosable) {
      this.removeModal();
    }
  }

  protected save() {
    this.mpModalService.save();
  }

  private computePosition() {
    this.ngZone.runOutsideAngular(() => {
      if (this.mpMode !== 'floating') {
        return;
      }

      const { left, right, bottom } = this.anchor.nativeElement.getBoundingClientRect();
      const maxWidth = window.innerWidth;
      const maxHeight = window.innerHeight;
      const modalMeasure = this.modalElementRef.nativeElement.getBoundingClientRect();

      if (bottom + modalMeasure.height > maxHeight - 14) {
        this.modalElementRef.nativeElement.style.bottom = '10px';
      } else {
        this.modalElementRef.nativeElement.style.top = `${bottom + 4}px`;
      }

      if (right > maxWidth - 10) {
        this.modalElementRef.nativeElement.style.right = '10px';
      } else {
        this.modalElementRef.nativeElement.style.left = `${left}px`;
      }
    });
  }

  private initProjectedContentInjector(): void {
    this.projectedInjector = Injector.create({
      providers: [
        { provide: MpModalService, useValue: this.mpModalService },
        { provide: MP_MODAL_DATA, useValue: this.mpModalData }
      ],
      parent: this.injector
    });
  }
}
